import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export type ErrorLogStatus = 'open' | 'fixed' | 'ignored';
export type ErrorTriageVerdict = 'noise' | 'code_bug' | 'infra' | 'data' | 'external' | 'needs_human';
export type ErrorTriageConfidence = 'low' | 'medium' | 'high';

export interface ErrorLogEntry {
  id: number;
  source: string;
  context: string | null;
  message: string;
  detail: Record<string, unknown> | null;
  occurrences: number;
  first_seen_at: string;
  last_seen_at: string;
  status: ErrorLogStatus;
  note: string | null;
  resolved_at: string | null;
  // Automated triage (changeset 0059); all null until the daily triage has run.
  triage_verdict: ErrorTriageVerdict | null;
  triage_confidence: ErrorTriageConfidence | null;
  triage_summary: string | null;
  triage_suggestion: string | null;
  triage_pr_url: string | null;
  triaged_at: string | null;
}

export interface ErrorLogResponse {
  entries: ErrorLogEntry[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface ErrorLogFilters {
  status?: string;
  source?: string;
  /** A triage verdict, or 'untriaged' for errors not yet triaged. */
  verdict?: ErrorTriageVerdict | 'untriaged';
}

export function useErrorLog(page: number, filters: ErrorLogFilters = {}, limit = 50) {
  return useQuery({
    queryKey: ['error-log', page, limit, filters],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (filters.status) params.set('status', filters.status);
      if (filters.source) params.set('source', filters.source);
      if (filters.verdict) params.set('verdict', filters.verdict);
      return apiClient<ErrorLogResponse>(`/error-log?${params.toString()}`);
    },
  });
}

export function useResolveError() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: number; status: 'fixed' | 'ignored'; note?: string }) =>
      apiClient<ErrorLogEntry>(`/error-log/${id}`, { method: 'PATCH', body: JSON.stringify({ status, note }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['error-log'] }),
  });
}
